import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API_STATUS, hasUserRegisteredToSession, isComplete } from 'store/constants';
import {
	addUserSessionFeedback,
	fetchUserSessions,
	registerUserToSession,
	unregisterUserToSession
} from './userSessionsAPI';

const initialState = {
	sessions: [],
	status: ''
};

// thunk & service
export const getUserSessionsAsync = createAsyncThunk('userSessions/fetchUserSessions', async () => {
	const userSessions = await fetchUserSessions();
	return userSessions;
});

export const registerUserToSessionAsync = createAsyncThunk('userSessions/registerUserToSession', async eventId => {
	const sessionData = await registerUserToSession(eventId);
	// The value we return becomes the `fulfilled` action payload
	return sessionData;
});

export const unregisterUserToSessionAsync = createAsyncThunk(
	'userSessions/unregisterUserToSession',
	async registrationData => {
		const { eventId } = registrationData;
		const sessionData = await unregisterUserToSession(eventId);
		// The value we return becomes the `fulfilled` action payload
		return sessionData;
	}
);

export const addUserSessionFeedbackAsync = createAsyncThunk('guest/addUserSessionFeedback', async registrationData => {
	const { eventId, feedback } = registrationData;
	const sessionFeedback = await addUserSessionFeedback(eventId, feedback);
	// The value we return becomes the `fulfilled` action payload
	return sessionFeedback;
});

export const userSessionsSlice = createSlice({
	name: 'userSessions',
	initialState,
	reducers: {
		setUserSessions: (state, action) => {
			// TODO: setter
		}
	},

	extraReducers: builder => {
		builder
			.addCase(getUserSessionsAsync.pending, state => {
				state.status = API_STATUS.PENDING;
			})
			.addCase(getUserSessionsAsync.rejected, state => {
				state.status = API_STATUS.REJECTED;
			})
			.addCase(getUserSessionsAsync.fulfilled, (state, action) => {
				state.sessions = [...action.payload];
				state.status = API_STATUS.COMPLETED;
			})
			.addCase(registerUserToSessionAsync.pending, state => {
				state.registeredStatus = API_STATUS.PENDING;
			})
			.addCase(registerUserToSessionAsync.rejected, state => {
				state.registeredStatus = API_STATUS.REJECTED;
			})
			.addCase(registerUserToSessionAsync.fulfilled, (state, action) => {
				state.sessions.push(action.payload);
				state.registeredStatus = API_STATUS.COMPLETED;
			})
			.addCase(unregisterUserToSessionAsync.pending, state => {
				state.registeredStatus = API_STATUS.PENDING;
			})
			.addCase(unregisterUserToSessionAsync.rejected, state => {
				state.registeredStatus = API_STATUS.REJECTED;
			})
			.addCase(unregisterUserToSessionAsync.fulfilled, (state, action) => {
				state.sessions = state.sessions.filter(ue => ue.eventId === action.payload.evenId);
				state.registeredStatus = API_STATUS.COMPLETED;
			})
			.addCase(addUserSessionFeedbackAsync.rejected, state => {
				state.feedbackStatus = API_STATUS.REJECTED;
			})
			.addCase(addUserSessionFeedbackAsync.fulfilled, (state, action) => {
				state.sessions = state.sessions.map(session => {
					if (session.eventId !== action.payload.eventId) {
						return session;
					}

					return Object.assign({}, session, {
						...action.payload
					});
				});
				state.feedbackStatus = API_STATUS.COMPLETED;
			});
	}
});

export const selectUserSessions = state => state.userSessions.sessions;
export const selectUserSessionsLoaded = state => {
	return isComplete(state.userSessions.status);
};
export const selectHasUserRegisteredToSession = sessionId => state => {
	const exists = state.userSessions?.sessions.find(e => e.eventId === sessionId);
	return hasUserRegisteredToSession(exists?.status);
};
export const selectGetUserRegisteredDate = sessionId => state => {
	const exists = state.userSessions?.sessions.find(e => e.eventId === sessionId);
	return exists?.registeredDate;
};
export const isUserSessionFeedbackComplete = sessionId => state => {
	const exists = state.userSessions?.sessions.find(e => e.eventId === sessionId);
	return !!exists?.feedback;
};

export const { setUserSessions } = userSessionsSlice.actions;

export default userSessionsSlice.reducer;
